/**
 * W7 PWA：自包含的「安装到桌面」入口。
 *
 * 监听浏览器的 beforeinstallprompt（Chrome/Edge 在判定可安装时触发），先拦下默认的
 * mini-infobar，由我们在合适时机用一个小按钮触发原生安装确认。装好后（appinstalled）
 * 或已经是独立窗口运行时（display-mode: standalone）都不再显示。
 *
 * 样式刻意用最小内联、中性配色 —— 不进 global.css，不依赖主题 token，方便和 UI 皮肤分支
 * 合并时零冲突；皮肤定稿后可整体替换这里的视觉。
 *
 * Safari（含桌面）不支持 beforeinstallprompt，所以那边永远不显示（已知限制）。
 */
import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // 已经在独立窗口里跑（= 已安装）就不提示。
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setDeferred(null);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!deferred || dismissed) return null;

  return (
    <div
      role="dialog"
      aria-label="安装到桌面"
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 10px 8px 14px',
        background: '#FDFDF9',
        border: '1.5px solid #111',
        borderRadius: 12,
        boxShadow: '2px 3px 0 rgba(17,17,17,0.18)',
        fontSize: 13,
        color: '#1a1a1a',
        maxWidth: 280,
      }}
    >
      <span>装到桌面，下次点图标直接打开，不用再开浏览器输网址。</span>
      <button
        type="button"
        onClick={async () => {
          const e = deferred;
          setDeferred(null);
          try {
            await e.prompt();
            await e.userChoice;
          } catch {
            /* 用户取消或浏览器拒绝，静默 */
          }
        }}
        style={{
          flex: 'none',
          padding: '4px 10px',
          background: '#C6E84F',
          border: '1.5px solid #111',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        安装
      </button>
      <button
        type="button"
        aria-label="先不用"
        onClick={() => setDismissed(true)}
        style={{
          flex: 'none',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 16,
          lineHeight: 1,
          color: '#888',
        }}
      >
        ×
      </button>
    </div>
  );
}
